import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessTaskStatusType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import TaskStatusIcon, {TASK_STATUS_ICONS} from 'processes/status/TaskStatusIcon';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import {StudyForOverview} from '../utils/TypedStudyTypes';
import StudyDesignTabButton from '../utils/ui/StudyDesignTabButton';
import {
  StudyInaccessibleContentBanner,
  StudyMissingContentBanner,
  StudyQueryErrorBanner,
} from '../utils/ui/StudyQueryBanners';
import {
  ProcessDevelopmentStudyDesignOverviewQuery,
  ProcessDevelopmentStudyDesignOverviewQueryVariables,
} from './__generated/ProcessDevelopmentStudyDesignOverviewQuery';

export const PROCESS_DEVELOPMENT_STUDY_DESIGN_OVERVIEW_QUERY: DocumentNode = gql`
  query ProcessDevelopmentStudyDesignOverviewQuery($studyId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          ... on StudySchemaNode {
            id
            studyType
          }
        }
        procedureRun {
          ... on ProcedureRunNode {
            id
            conditions {
              id
            }
            templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                nodes {
                  id
                }
                procedureFlowchart {
                  ... on ProcedureFlowchartNode {
                    id
                    parent {
                      id
                    }
                  }
                }
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingProcessDevelopmentStudyDesignOverview({
  study,
}: {
  study: StudyForOverview;
}): JSX.Element | null {
  const result = useBenchlingQuery<
    ProcessDevelopmentStudyDesignOverviewQuery,
    ProcessDevelopmentStudyDesignOverviewQueryVariables
  >(PROCESS_DEVELOPMENT_STUDY_DESIGN_OVERVIEW_QUERY, {
    variables: {studyId: study.id},
  });
  if (result.type === QueryResultType.ERROR) {
    return <StudyQueryErrorBanner />;
  } else if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else {
    const {data} = result;
    if (isInaccessible(data.study)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study}
          modelType={ModelTypeConstants.STUDY}
          displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
        />
      );
    } else if (data.study.procedureRun == null) {
      return (
        <StudyMissingContentBanner
          parentRenderFunctionName={ProcessDevelopmentStudyDesignOverview.name}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          parentId={study.id}
          displayErrorText="The experiment run associated with this study could not be found."
        />
      );
    } else if (isInaccessible(data.study.procedureRun)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study.procedureRun}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          displayErrorText="The experiment run of this study is not accessible. The experiment run associated with this study may have been moved into a project that you do not have access to."
        />
      );
    } else if (isInaccessible(data.study.procedureRun.templateProcedureFlowchartVersion)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.study.procedureRun.templateProcedureFlowchartVersion}
          modelType={ModelTypeConstants.PROCEDURE_FLOWCHART_VERSION}
          displayErrorText="The recipe of this study is not accessible. The recipe associated with this study may have been moved into a collection that you do not have access to."
        />
      );
    } else if (data.study.procedureRun.templateProcedureFlowchartVersion.procedureFlowchart.parent == null) {
      return (
        <StudyMissingContentBanner
          parentRenderFunctionName={ProcessDevelopmentStudyDesignOverview.name}
          modelType={ModelTypeConstants.PROCEDURE_FLOWCHART}
          parentId={study.id}
          displayErrorText="The recipe associated with this study could not be found."
        />
      );
    } else {
      return (
        <ProcessDevelopmentStudyDesignOverview
          studyId={data.study.id}
          studyType={data.study.studySchema.studyType}
          procedureId={data.study.procedureRun.templateProcedureFlowchartVersion.procedureFlowchart.parent.id}
          numUnitOperations={data.study.procedureRun.templateProcedureFlowchartVersion.nodes.length}
          numConditions={data.study.procedureRun.conditions.length}
        />
      );
    }
  }
}

function ProcessDevelopmentStudyDesignOverview(props: {
  studyId: string;
  studyType: StudyType;
  procedureId: string;
  numUnitOperations: number;
  numConditions: number;
}): JSX.Element {
  const designStatus = ProcessTaskStatusType.COMPLETED;
  return (
    <div className={css(styles.gridContainer)}>
      {/* ========== Header ========== */}
      <div>Design status</div>
      <div style={{gridColumnStart: 2, gridColumnEnd: 6}}>Recipe</div>
      {/* ========== Body ========== */}
      <div>
        <TaskStatusIcon type={designStatus} />
        {TASK_STATUS_ICONS[designStatus].name}
      </div>
      <div style={{gridColumnStart: 2, gridColumnEnd: 4}}>
        <BenchChip id={props.procedureId} size={ChipSize.MINI} />
      </div>
      <div style={{gridColumnStart: 4, gridColumnEnd: 6}} className={css(styles.designButtonCell)}>
        <StudyDesignTabButton studyId={props.studyId} studyType={props.studyType} />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: 10,
    paddingBottom: 10,
  },
  designButtonCell: {
    textAlign: 'right',
    paddingRight: 10,
  },
});
